import React, { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerActions, DrawerView } from '@divvy-web/skylab.drawer'
import { FormTextInput } from '@divvy-web/skylab.form'
import { FormProvider, useForm, useFormState } from '@divvy-web/skylab.react-hook-form'
import Scrim from '@divvy-web/skylab.scrim'
import { TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import Collaborator from '@sputnik/components/InviteCollaborators/Collaborator'
import useApplicationCollaborators from '@sputnik/hooks/useApplicationCollaborators'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useInviteUsers } from './InviteUsers.gql'
import styles from './styles.module.scss'
import { useInviteCollaboratorsSchema } from './useInviteCollaboratorsSchema'

export interface InviteCollaboratorsDrawerProps {
  appId: string
  isShowing: boolean
  onClose: () => void
  headerLeftIcon?: 'close' | 'back'
}

const InviteCollaboratorsDrawer = ({
  appId,
  isShowing,
  onClose,
  headerLeftIcon = 'close',
}: InviteCollaboratorsDrawerProps) => {
  const [getClassName, makeTestId] = useNameFormatter('InviteCollaboratorsDrawer')
  const showToast = useToast()

  const [inviteUsers] = useInviteUsers({
    refetchQueries: ['GetApplicationEmails'],
  })

  const { allCollaborators } = useApplicationCollaborators({ salesforceCreditId: appId, skip: !isShowing })

  const schema = useInviteCollaboratorsSchema({ collaborators: allCollaborators })

  type Schema = yup.InferType<typeof schema>

  const formMethods = useForm<Schema>({
    mode: 'all',
    defaultValues: {
      emails: '',
    },
    resolver: yupResolver(schema),
  })

  const { handleSubmit, control, reset } = formMethods
  const { isDirty, isValid } = useFormState({ control })

  const handleInviteSubmit = useCallback(
    async (data: Schema) => {
      const emailEntries = data.emails.split(',').map((email) => email.trim())

      await inviteUsers({
        variables: {
          creditApplicationId: appId,
          emails: emailEntries,
        },
        onCompleted: () => {
          logInfo('inviteCollaborators', {
            attributes: {
              creditAppId: appId,
              numberOfInvites: emailEntries.length,
              result: 'collaborators invited',
            },
            eventName: 'inviteCollaboratorsSuccess',
          })

          showToast(
            TOAST_TYPE_SUCCESS,
            <FormattedMessage
              defaultMessage='Invites successfully sent'
              id='sputnik.InviteCollaboratorsDrawer__ixk27n'
            />,
          )
          reset()
        },
        onError: (error) => {
          logError(error, {
            attributes: {
              action: 'inviteCollaborators',
              creditAppId: appId,
              message: error?.message,
              numberOfInvites: emailEntries.length,
              result: 'failed to invite collaborators',
            },
            eventName: 'inviteCollaboratorsFailure',
          })
          showToast(
            TOAST_TYPE_SUCCESS,
            <FormattedMessage
              defaultMessage='Failed to invite collaborators'
              id='sputnik.InviteCollaboratorsDrawer__011Ad8'
            />,
          )
        },
      })
    },
    [appId, inviteUsers, showToast, reset],
  )

  return (
    <Scrim isShowing={isShowing}>
      <Drawer
        activeViewKey='default'
        className={styles[getClassName('')]}
        dataTestId={makeTestId('')}
        isShowing={isShowing}
        views={{
          default: (
            <DrawerView
              footerActions={
                <DrawerActions
                  cancelText={
                    <FormattedMessage
                      defaultMessage='Cancel'
                      id='sputnik.InviteCollaboratorsDrawer__47FYwb'
                    />
                  }
                  confirmButtonColor='action'
                  confirmText={
                    <FormattedMessage
                      defaultMessage='Invite'
                      id='sputnik.InviteCollaboratorsDrawer__hYOE+U'
                    />
                  }
                  inactive={!isValid || !isDirty}
                  onCancel={onClose}
                  onConfirm={handleSubmit(handleInviteSubmit)}
                />
              }
              headerLeftIcon={headerLeftIcon}
              headerLeftIconName={headerLeftIcon}
              title={
                <FormattedMessage
                  defaultMessage='Invite collaborators'
                  id='sputnik.InviteCollaboratorsDrawer__ni18Y2'
                />
              }
              onLeftIconClick={onClose}
            >
              <FormProvider {...formMethods}>
                <form
                  className={`${styles[getClassName('form')]} fs-unmask`}
                  onSubmit={handleSubmit(handleInviteSubmit)}
                >
                  <div className={styles[getClassName('subheading')]}>
                    <FormattedMessage
                      defaultMessage='Invite a collaborator to help you fill out this application.'
                      id='sputnik.InviteCollaboratorsDrawer__zYQ/Sf'
                    />
                  </div>
                  <FormTextInput
                    caption={
                      <FormattedMessage
                        defaultMessage='Separate multiple emails with commas'
                        id='sputnik.InviteCollaboratorsDrawer__r43jfV'
                      />
                    }
                    className='fs-mask'
                    control={control}
                    label={
                      <FormattedMessage
                        defaultMessage='Enter collaborator email'
                        id='sputnik.InviteCollaboratorsDrawer__t8gSjR'
                      />
                    }
                    name='emails'
                  />
                  {allCollaborators.length > 0 && (
                    <div className={styles[getClassName('collaborators')]}>
                      <div className={styles[getClassName('collaborators-title')]}>
                        <FormattedMessage
                          defaultMessage='Current collaborators'
                          id='sputnik.InviteCollaboratorsDrawer__iSeTDg'
                        />
                      </div>
                      {allCollaborators.map(({ email, firstName, lastName, role }) => (
                        <span
                          key={email}
                          className='fs-mask'
                        >
                          <Collaborator
                            key={email}
                            appId={appId}
                            email={email}
                            firstName={firstName}
                            lastName={lastName}
                            role={role}
                          />
                        </span>
                      ))}
                      <div className={styles[getClassName('collaborators-delete-info')]}>
                        <FormattedMessage
                          defaultMessage='If you remove a collaborator, they’ll no longer have access to this application. But the information they entered will be saved.'
                          id='sputnik.InviteCollaboratorsDrawer__On/dYy'
                        />
                      </div>
                    </div>
                  )}
                </form>
              </FormProvider>
            </DrawerView>
          ),
        }}
      />
    </Scrim>
  )
}

export default InviteCollaboratorsDrawer
